import { useState } from 'react';

import { LucideIcon } from 'lucide-react';

import { Separator } from '@/components/ui/separator';
import { cn } from '@/lib/utils';

import { ClearFiltersButton } from './ClearFiltersButton';
import { DeleteConfirmDialog } from './DeleteConfirmDialog';
import { FilterPillPopover } from './FilterPillPopover';
import { RenameDeletePopover } from './RenameDeletePopover';
import { SavedFilterSelector } from './SavedFilterSelector';
import { SavePopover } from './SavePopover';
import { FilterInputType, FilterOption } from './types';

interface FilterBadge {
  id: string;
  icon: LucideIcon;
  title: string;
  text: string;
  onRemove: () => void;
  filterDefinition: {
    overlayLabel?: string;
    type: FilterInputType;
    options: FilterOption[];
    placeholder?: string;
    condition?: boolean;
  };
  value: string | string[];
  onValueChange: (value: string | string[] | undefined, exclude?: boolean) => void;
  exclude?: boolean;
}

export interface SavedFilterView {
  id: string;
  name: string;
}

interface SavedFiltersConfig {
  savedFilters: SavedFilterView[];
  activeSavedFilter: SavedFilterView | null;
  hasUnsavedChanges: boolean;
  onSave: (name: string) => string | null;
  onUpdate: () => void;
  onDelete: () => void;
  onRename: (name: string) => string | null;
  onSelectView?: (viewId: string) => void;
}

interface ComplexFilterBarProps {
  filters?: FilterBadge[];
  savedFiltersConfig?: SavedFiltersConfig;
  openPillId?: string | null;
  onOpenPillChange?: (id: string | null) => void;
  onClearAll?: () => void;
  className?: string;
  /** Show the "Clear · All" bulk actions inside each filter pill's popover. Defaults to true. */
  showBulkActions?: boolean;
}

export const ComplexFilterBar = ({
  filters,
  savedFiltersConfig,
  openPillId,
  onOpenPillChange,
  onClearAll,
  className,
  showBulkActions,
}: ComplexFilterBarProps) => {
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [internalOpenFilterId, setInternalOpenFilterId] = useState<string | null>(null);
  const [isSelectOpen, setIsSelectOpen] = useState(false);

  const {
    savedFilters = [],
    activeSavedFilter,
    hasUnsavedChanges,
    onSave,
    onUpdate,
    onDelete,
    onRename,
    onSelectView,
  } = savedFiltersConfig || {};

  // Use controlled state if provided, otherwise use internal state
  const openFilterId = openPillId !== undefined ? openPillId : internalOpenFilterId;
  const setOpenFilterId = onOpenPillChange || setInternalOpenFilterId;

  const hasFilters = filters && filters.length > 0;
  const hasVisibleSavedFilter =
    savedFiltersConfig?.activeSavedFilter && !savedFiltersConfig?.hasUnsavedChanges;

  // Show save button when there are filters AND (no active view OR has unsaved changes)
  const showSaveButton =
    savedFiltersConfig && hasFilters && (!activeSavedFilter || hasUnsavedChanges);

  // Show rename/delete menu when there's an active saved filter with NO unsaved changes
  const showRenameMenu = savedFiltersConfig && activeSavedFilter && !hasUnsavedChanges;

  // Action buttons render in an absolute-positioned row; reserve right padding so pills don't slide under them
  const hasActions = Boolean(showSaveButton || showRenameMenu || onClearAll);

  // If there's no filters and no active saved filter, render nothing
  const hasContent = hasFilters || hasVisibleSavedFilter;
  if (!hasContent) {
    return null;
  }

  return (
    <div
      className={cn(
        'relative flex min-h-9 w-full rounded-lg border border-stroke bg-background p-1.5',
        className
      )}
    >
      <div className={cn('flex flex-1 flex-wrap items-center gap-2', hasActions && 'pr-20')}>
        {/* Active saved filter*/}
        {activeSavedFilter && (
          <>
            <SavedFilterSelector
              savedFilters={savedFilters}
              activeSavedFilter={activeSavedFilter}
              onSelectView={onSelectView}
            />
            <Separator orientation="vertical" className="h-8" />
          </>
        )}

        {/* Filter pills */}
        {filters?.map((filter) => (
          <FilterPillPopover
            key={filter.id}
            icon={filter.icon}
            title={filter.title}
            text={filter.text}
            isOpen={openFilterId === filter.id}
            onOpenChange={(open) => setOpenFilterId(open ? filter.id : null)}
            onRemove={() => {
              setOpenFilterId(null);
              filter.onRemove();
            }}
            filterDefinition={filter.filterDefinition}
            value={filter.value}
            onValueChange={filter.onValueChange}
            exclude={filter.exclude}
            isSelectOpen={isSelectOpen}
            onSelectOpenChange={setIsSelectOpen}
            showBulkActions={showBulkActions}
          />
        ))}
      </div>

      {hasActions && (
        <div className="absolute top-1.5 right-1.5 flex items-center gap-1.5">
          {onClearAll && (
            <ClearFiltersButton onClick={onClearAll} activeFilterCount={filters?.length ?? 0} />
          )}
          {showSaveButton && onSave && (
            <SavePopover hasActiveView={!!activeSavedFilter} onSave={onSave} onUpdate={onUpdate} />
          )}
          {showRenameMenu && (
            <RenameDeletePopover
              currentName={activeSavedFilter?.name}
              onRename={onRename}
              onDeleteClick={() => setDeleteConfirmOpen(true)}
            />
          )}
        </div>
      )}

      <DeleteConfirmDialog
        open={deleteConfirmOpen}
        onOpenChange={setDeleteConfirmOpen}
        filterName={activeSavedFilter?.name}
        onConfirm={() => onDelete?.()}
      />
    </div>
  );
};
